import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Wallet } from "lucide-react";

// Import modular constants and context
import { AppContext, useApp } from "./contexts/AppContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { BURNZ_LOGO } from "./constants";
import { DEMO } from "./constants/mockData";

// Import modular layout components
import { TopHeader, LeftSidebar } from "./components/layout";
import { MobileNav } from "./components/layout/MobileNav";

// Import pages
import { 
  CommandCenter, 
  CampaignStudioPage, 
  RewardsPage, 
  TeamsPage, 
  QuestsPage, 
  ProfilePage, 
  DashboardPage, 
  LandingPage,
  DumpPage,
  ExchangePage,
  ImpactPage,
  RequestPoolPage,
  ReferralDashboard
} from "./pages";

// Import API service
import { authAPI } from "./services/api";

// Wallet icons mapping
const walletIcons = {
  nami: "🦊",
  eternl: "🔷",
  flint: "🔥",
  lace: "💎",
  yoroi: "🔵",
  typhon: "🌊",
  gerowallet: "🟢",
};

// Wallet Modal with Real Cardano Integration
const WalletConnectModal = ({ open, onOpenChange, onConnect }) => {
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [availableWallets, setAvailableWallets] = useState([]);

  useEffect(() => {
    // Check for available Cardano wallets
    const checkWallets = async () => {
      const wallets = [];
      if (typeof window !== 'undefined' && window.cardano) {
        const walletNames = ['nami', 'eternl', 'flint', 'lace', 'yoroi', 'typhon', 'gerowallet'];
        for (const name of walletNames) {
          if (window.cardano[name]) {
            wallets.push({
              name: name.charAt(0).toUpperCase() + name.slice(1),
              id: name,
              icon: walletIcons[name] || "💳"
            });
          }
        }
      }
      setAvailableWallets(wallets);
    };
    
    if (open) {
      checkWallets();
      setError(null);
    }
  }, [open]);

  const handleWalletConnect = async (walletId) => {
    setConnecting(true);
    setError(null);
    
    try {
      if (!window.cardano || !window.cardano[walletId]) {
        throw new Error(`${walletId} wallet not found. Please install it first.`);
      }
      
      // Enable the wallet
      const wallet = await window.cardano[walletId].enable();
      
      // Get the wallet address
      const addresses = await wallet.getUsedAddresses();
      const address = addresses[0] || (await wallet.getChangeAddress());
      
      // Decode the address from hex if needed
      let walletAddress = address;
      if (address && !address.startsWith('addr')) {
        walletAddress = address;
      }
      
      // Try to register/login with the backend
      let userData = null;
      try {
        console.log('[BURNZ DEBUG] Calling authAPI.connect with:', walletAddress);
        const response = await authAPI.connect(walletAddress, walletId);
        console.log('[BURNZ DEBUG] authAPI.connect response:', response);
        if (response.access_token) {
          localStorage.setItem('burnz_token', response.access_token);
          userData = response.user;
          console.log('[BURNZ DEBUG] userData from API:', userData);
        }
      } catch (apiErr) {
        console.error('[BURNZ DEBUG] authAPI.connect error:', apiErr);
        // If connection fails, continue with local storage for demo
        localStorage.setItem('burnz_token', `demo_token_${walletAddress}`);
      }
      
      // Call the onConnect callback with user data
      console.log('[BURNZ DEBUG] Calling onConnect with userData:', userData);
      await onConnect(walletAddress, walletId, userData);
      onOpenChange(false);
      
    } catch (err) {
      setError(err.message || 'Failed to connect wallet');
    } finally {
      setConnecting(false);
    }
  };
  
  const handleDemo = async () => {
    setConnecting(true);
    const demoAddress = "addr1qx" + Math.random().toString(36).substring(2, 15) + "...demo";
    localStorage.setItem("burnz_token", "demo_token");
    await onConnect(demoAddress, "demo");
    onOpenChange(false);
    setConnecting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface-2 border-white/10 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white text-xl">Connect Wallet</DialogTitle>
          <DialogDescription className="text-text-secondary">
            Connect your Cardano wallet to start earning XP & BURNZ
          </DialogDescription>
        </DialogHeader>
        
        {error && (
          <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}
        
        <div className="space-y-3 py-4">
          {availableWallets.length > 0 ? (
            <>
              <p className="text-text-secondary text-sm">Available wallets:</p>
              {availableWallets.map(wallet => (
                <button 
                  key={wallet.id} 
                  onClick={() => handleWalletConnect(wallet.id)}
                  disabled={connecting}
                  className="w-full flex items-center gap-3 p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-all disabled:opacity-50"
                >
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-xl">
                    {wallet.icon}
                  </div>
                  <span className="text-white font-medium">{wallet.name}</span>
                  {connecting && <span className="ml-auto text-text-secondary text-sm">Connecting...</span>}
                </button>
              ))}
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-text-secondary mb-4">No Cardano wallets detected</p>
              <div className="space-y-2 text-sm">
                <a href="https://namiwallet.io" target="_blank" rel="noopener noreferrer" className="block text-secondary-theme hover:opacity-80">
                  → Install Nami Wallet
                </a>
                <a href="https://eternl.io" target="_blank" rel="noopener noreferrer" className="block text-secondary-theme hover:opacity-80">
                  → Install Eternl Wallet
                </a>
                <a href="https://flint-wallet.com" target="_blank" rel="noopener noreferrer" className="block text-secondary-theme hover:opacity-80">
                  → Install Flint Wallet
                </a>
              </div>
            </div>
          )}
          
          <Separator className="bg-white/10" />
          
          <Button 
            className="w-full bg-primary-theme hover:opacity-90 text-white h-12" 
            onClick={handleDemo} 
            disabled={connecting}
          >
            {connecting ? "Connecting..." : "🎮 Demo Mode (No Wallet)"}
          </Button>
          
          <p className="text-text-muted text-xs text-center">
            Demo mode lets you explore all features with simulated data
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Connect Wallet Prompt Component
const ConnectWalletPrompt = ({ message }) => {
  const { setWalletModalOpen } = useApp();
  
  return (
    <Card className="bg-gradient-to-br from-primary-theme/10 to-secondary-theme/10 border-primary-theme/30">
      <CardContent className="p-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-theme to-secondary-theme flex items-center justify-center mx-auto mb-4">
          <Wallet className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Connect Your Wallet</h3>
        <p className="text-text-secondary mb-6">{message || "Connect your wallet to access this feature and start earning XP & BURNZ"}</p>
        <Button onClick={() => setWalletModalOpen(true)} className="bg-primary-theme hover:opacity-90 text-white">
          <Wallet className="w-4 h-4 mr-2" />Connect Wallet
        </Button>
      </CardContent>
    </Card>
  );
};

// Main App
function App() {
  // Super admin wallet addresses - must match backend exactly
  const SUPER_ADMIN_WALLETS = [
    "addr1q9ux67lgxrejk752rtwr9gkm24lrthr3td7k7yexq7ms59tqrg557stwp8hwd5eclku7t6wq46gc72na8fwgj22c8e5qurvush",
    "addr1qxc6u2d5ppeuhg7tkj5quf3csxp2022g98az65hpad9m6yreuxes6p3hhg880tegsg95xqwek6srgac5jq04x9rcm2qq6a6md7"
  ];

  // Initialize state from localStorage - prioritize stored user data
  const [user, setUser] = useState(() => {
    const token = localStorage.getItem("burnz_token");
    if (!token) return null;
    
    // Try to get stored user data first (most reliable)
    const storedUser = localStorage.getItem("burnz_user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        console.log('[BURNZ DEBUG] Restored user from localStorage:', parsedUser);
        return parsedUser;
      } catch (e) {
        console.error('[BURNZ DEBUG] Failed to parse stored user:', e);
      }
    }
    
    // Fallback: reconstruct from token (for backwards compatibility)
    const walletAddress = token.replace('demo_token_', '');
    // Use exact match against admin wallets
    const isSuperAdmin = SUPER_ADMIN_WALLETS.includes(walletAddress);
    
    console.log('[BURNZ DEBUG] Reconstructing user from token:', { walletAddress, isSuperAdmin });
    
    return {
      ...DEMO.user,
      wallet_address: walletAddress,
      is_admin: isSuperAdmin,
      is_superadmin: isSuperAdmin,
      role: isSuperAdmin ? 'super_admin' : 'user'
    };
  });
  const [loading, setLoading] = useState(false);
  const [walletModalOpen, setWalletModalOpen] = useState(false);

  const handleConnect = useCallback(async (walletAddress, walletType, userData) => { 
    console.log('[BURNZ DEBUG] handleConnect called with:', { walletAddress, walletType, userData });
    
    localStorage.setItem("burnz_token", "demo_token_" + walletAddress);
    
    // Use backend user data if available, otherwise check locally
    let isAdmin = false;
    let isSuperAdmin = false;
    
    if (userData) {
      // Backend returned user data - trust the backend flags
      isAdmin = userData.is_admin === true || userData.is_superadmin === true;
      isSuperAdmin = userData.is_superadmin === true;
      console.log('[BURNZ DEBUG] Using backend user data:', { isAdmin, isSuperAdmin });
    } else {
      // Fallback: check locally against admin wallet list
      isSuperAdmin = SUPER_ADMIN_WALLETS.includes(walletAddress);
      isAdmin = isSuperAdmin;
      console.log('[BURNZ DEBUG] Using local admin check:', { walletAddress, isSuperAdmin });
    }
    
    const newUser = { 
      ...DEMO.user,
      ...(userData || {}),
      wallet_address: walletAddress,
      is_admin: isAdmin,
      is_superadmin: isSuperAdmin,
      role: isSuperAdmin ? 'super_admin' : 'user'
    };
    
    // Store user data in localStorage for persistence
    localStorage.setItem("burnz_user", JSON.stringify(newUser));
    console.log('[BURNZ DEBUG] Setting user state:', newUser);
    
    setUser(newUser); 
  }, []);
  
  const handleLogout = useCallback(() => { 
    // Clear all stored data
    localStorage.removeItem("burnz_token");
    localStorage.removeItem("burnz_user");
    localStorage.removeItem("burnz_wallet");
    
    // Clear user state
    setUser(null); 
    
    // Open wallet modal for reconnection
    setWalletModalOpen(true);
  }, []);

  const handleDisconnect = useCallback(() => {
    // Clear all stored data
    localStorage.removeItem("burnz_token");
    localStorage.removeItem("burnz_user");
    localStorage.removeItem("burnz_wallet");
    
    // Clear user state
    setUser(null);
  }, []);

  const handleReconnect = useCallback(() => {
    // Clear existing connection first
    localStorage.removeItem("burnz_token");
    localStorage.removeItem("burnz_user");
    localStorage.removeItem("burnz_wallet");
    setUser(null);
    
    // Open wallet modal for fresh connection
    setWalletModalOpen(true);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-1 flex items-center justify-center">
        <img src={BURNZ_LOGO} alt="BURNZ" className="h-16 w-auto animate-pulse" />
      </div>
    );
  }

  return (
    <ThemeProvider>
      <AppContext.Provider value={{ user, setUser, walletModalOpen, setWalletModalOpen, handleConnect, handleLogout, handleDisconnect, handleReconnect }}>
        <BrowserRouter>
          <Routes>
            {/* Landing page as home */}
            <Route path="/" element={<LandingPage />} />
            
            {/* Command Center - Admin/Superadmin only - redirects to /app if not admin */}
            <Route path="/command-center" element={
              user?.is_admin || user?.is_superadmin ? (
                <CommandCenter />
              ) : (
                <Navigate to="/app" replace />
              )
            } />
            
            {/* App routes - accessible without login but some features require connection */}
            <Route path="/app/*" element={
              <div className="min-h-screen bg-surface-1">
                <TopHeader user={user} />
                {/* Desktop Sidebar - Hidden on mobile */}
                <div className="hidden lg:block">
                  <LeftSidebar />
                </div>
                {/* Main content - Full width on mobile, offset on desktop */}
                <main className="lg:ml-48 p-4 lg:p-6 pb-28 lg:pb-6">
                  <Routes>
                    <Route path="/" element={<DashboardPage />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/teams" element={<TeamsPage />} />
                    <Route path="/pools" element={<TeamsPage />} />
                    <Route path="/quests" element={<QuestsPage />} />
                    <Route path="/dump" element={<DumpPage />} />
                    <Route path="/exchange" element={<ExchangePage />} />
                    <Route path="/campaign-studio" element={<CampaignStudioPage />} />
                    <Route path="/partner" element={<CampaignStudioPage />} />
                    <Route path="/rewards" element={<RewardsPage />} />
                    <Route path="/impact" element={<ImpactPage />} />
                    <Route path="/request" element={<RequestPoolPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/referrals" element={<ReferralDashboard />} />
                  </Routes>
                </main>
                {/* Mobile Bottom Navigation */}
                <MobileNav />
              </div>
            } />
            
            {/* Redirect old routes to /app */}
            <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/teams" element={<Navigate to="/app/teams" replace />} />
            <Route path="/pools" element={<Navigate to="/app/teams" replace />} />
            <Route path="/quests" element={<Navigate to="/app/quests" replace />} />
            <Route path="/dump" element={<Navigate to="/app/dump" replace />} />
            <Route path="/exchange" element={<Navigate to="/app/exchange" replace />} />
            <Route path="/partner" element={<Navigate to="/app/campaign-studio" replace />} />
            <Route path="/campaign-studio" element={<Navigate to="/app/campaign-studio" replace />} />
            <Route path="/impact" element={<Navigate to="/app/impact" replace />} />
            <Route path="/request" element={<Navigate to="/app/request" replace />} />
            <Route path="/profile" element={<Navigate to="/app/profile" replace />} />
            <Route path="/referrals" element={<Navigate to="/app/referrals" replace />} />
          </Routes>
          <WalletConnectModal open={walletModalOpen} onOpenChange={setWalletModalOpen} onConnect={handleConnect} />
        </BrowserRouter>
      </AppContext.Provider>
    </ThemeProvider>
  );
}

export default App;
